import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  SURVEY_SERVICE_URL,
  TargetSetting
} from '@insite-store360/shared';
import { catchError, Observable } from 'rxjs';
import { TARGET_SETTING_URL } from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class TargetSettingService {
  private readonly TARGET_SETTING_URL = `${TARGET_SETTING_URL}`;
  private readonly SURVEY_SERVICE_URL = `${SURVEY_SERVICE_URL}`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  //Get All Data
  getTargetSettings(): Observable<any> {
    return this.http.get<any>(`${this.TARGET_SETTING_URL}/getAllTargetIncentives`);
  }

  // Save the Row Data
  public saveTargetIncentive(formsData: TargetSetting): Observable<any> {
    return this.http
      .post<any>(`${this.TARGET_SETTING_URL}/save`, formsData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get All Active Status Names List
  public getAllActiveChannel(): Observable<any> {
    return this.http
      .get<any>(`${this.TARGET_SETTING_URL}/getActiveNames`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Delete the Row Entry
  public delete(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.TARGET_SETTING_URL}/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadExcel(id: number): Observable<Blob> {
    return this.http
      .get<Blob>(`${this.TARGET_SETTING_URL}/downloadExcelReport/${id}`, {
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //get target incentives based on id
  public getTargetIncentivesBasedonId(id: number): Observable<any> {
    return this.http
      .get<string>(`${this.TARGET_SETTING_URL}/getTargetIncentivesBasedonId/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  getProductGroup(): Observable<any> {
    return this.http.get<any>(`${this.TARGET_SETTING_URL}/getAllProductGroup`);
  }
  getUnityType(): Observable<any> {
    return this.http.get<any>(`${this.TARGET_SETTING_URL}/getAllUnitType`);
  }
  getCountingUnit(): Observable<any> {
    return this.http.get<any>(`${this.TARGET_SETTING_URL}/getAllCountingUnit`);
  }

  // Get all monthly promoter targets
  getMonthlyPromoterTargets(params?: any): Observable<any> {
    let queryParams = new URLSearchParams();

    if (params) {
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);
      if (params.searchName) queryParams.append('searchName', params.searchName);
      if (params.pageIndex) queryParams.append('pageIndex', params.pageIndex);
      if (params.pageSize) queryParams.append('pageSize', params.pageSize);
      if (params.targetTypeId) queryParams.append('targetTypeId', params.targetTypeId);
      if (params.countryId) queryParams.append('countryId', params.countryId);
    }

    const url = `${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/getAll${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get all monthly store targets
  getMonthlyStoreTargets(params?: any): Observable<any> {
    let queryParams = new URLSearchParams();

    if (params) {
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);
      if (params.searchName) queryParams.append('searchName', params.searchName);
      if (params.pageIndex) queryParams.append('pageIndex', params.pageIndex);
      if (params.pageSize) queryParams.append('pageSize', params.pageSize);
      if (params.targetTypeId) queryParams.append('targetTypeId', params.targetTypeId);
      if (params.countryId) queryParams.append('countryId', params.countryId);
    }

    const url = `${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/getAll/store${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Import target data
  importTargetData(data: any[], month: number, year: number, countryId?: number): Observable<any> {
    let queryParams = new HttpParams()
      .set('month', month)
      .set('year', year);
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/save`, data, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  //import target data for store
  importStoreTargetData(data: any[], month: number, year: number, countryId?: number): Observable<any> {
    let queryParams = new HttpParams()
      .set('month', month)
      .set('year', year);
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .post<any>(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/save/store`, data, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Download target template
  downloadTargetTemplate(month?: number, year?: number, targetTypeId?: number, countryId?: number): Observable<Blob> {
    let queryParams = new HttpParams();
    if (month && year) {
      queryParams = queryParams.set('month', month)
        .set('year', year);
    }
    if (targetTypeId) {
      queryParams = queryParams.set('targetTypeId', targetTypeId);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .post<Blob>(
        `${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/downloadExcelReport`,
        {
        },
        { params: queryParams, responseType: 'blob' as 'json' }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  //Download target template for store
  downloadStoreTargetTemplate(month?: number, year?: number, targetTypeId?: number, countryId?: number): Observable<Blob> {
    let queryParams = new HttpParams();

    if (month && year) {
      queryParams = queryParams.set('month', month)
        .set('year', year);
    }
    if (targetTypeId) {
      queryParams = queryParams.set('targetTypeId', targetTypeId);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }

    return this.http
      .post<Blob>(
        `${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/store/downloadExcelReport`,
        {}, // empty body
        {
          params: queryParams,
          responseType: 'blob' as 'json'
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }
  // Get all unity types
  getUnityTypes(): Observable<any> {
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/unityType/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Delete monthly promoter target
  deleteMonthlyPromoterTarget(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Delete monthly store target
  deleteMonthlyStoreTarget(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/delete/store/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateMonthlyPromoterTarget(id: number, data: { target: number; targetTypeId: number }, countryId: number): Observable<any> {
    return this.http.put(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/update/${id}`, data, { params: { countryId: countryId } });
  }

  public updateMonthlyStoreTarget(id: number, data: { id?: number; target: number }): Observable<any> {
    return this.http.put(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/update/store/${id}`, data);
  }

  // Export monthly promoter targets to Excel
  exportMonthlyPromoterTargets(params?: any): Observable<Blob> {
    let queryParams = new URLSearchParams();

    if (params) {
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);
      if (params.searchName) queryParams.append('searchName', params.searchName);
      if (params.targetTypeId) queryParams.append('targetTypeId', params.targetTypeId);
      if (params.isStore) queryParams.append('isStore', params.isStore);
      if (params.countryId) queryParams.append('countryId', params.countryId);
    }

    const url = `${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/view/downloadExcelReport${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return this.http
      .post<Blob>(url, {}, { responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Export monthly store targets to Excel
  exportMonthlyStoreTargets(params?: any): Observable<Blob> {
    let queryParams = new URLSearchParams();

    if (params) {
      if (params.month) queryParams.append('month', params.month);
      if (params.year) queryParams.append('year', params.year);
      if (params.searchName) queryParams.append('searchName', params.searchName);
      if (params.targetTypeId) queryParams.append('targetTypeId', params.targetTypeId);
      if (params.countryId) queryParams.append('countryId', params.countryId);
    }

    const url = `${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/view/downloadExcelReport/store${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

    return this.http
      .post<Blob>(url, {}, { responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }

  // Get target type summary
  getTargetTypeSummary(month: number, year: number, isStore: boolean, countryId?: number | number[] | string): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId !== undefined && countryId !== null && countryId !== '') {
      const value = Array.isArray(countryId) ? countryId.join(',') : String(countryId);
      if (value) {
        queryParams = queryParams.set('countryId', value);
      }
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/divideByTargetType?month=${month}&year=${year}&isStore=${isStore}`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  getStoreTargetSummary(month: number, year: number, countryId?: number | number[] | string, searchName?: string): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId !== undefined && countryId !== null && countryId !== '') {
      const value = Array.isArray(countryId) ? countryId.join(',') : String(countryId);
      if (value) {
        queryParams = queryParams.set('countryId', value);
      }
    }
    if (searchName) {
      queryParams = queryParams.set('searchName', searchName);
    }
    return this.http
      .get<any>(`${this.SURVEY_SERVICE_URL}/monthlyPromoterTarget/divideByTargetType/store?month=${month}&year=${year}`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }
}
